import { getComparisonRange, TrafficSourceCodeEnum } from '@metapic-apps/shared'
import dayjs from 'dayjs'
import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import type {
  PerformanceMetricsFilter,
  PerformanceTrafficSource,
} from '@/lib/types/performance.types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useGlobalFilterStore } from '@/stores/global-filter.stores'

const TRAFFIC_SOURCE_MAP: Record<TrafficSourceCodeEnum, PerformanceTrafficSource> = {
  [TrafficSourceCodeEnum.Instagram]: 'instagram',
  [TrafficSourceCodeEnum.Tiktok]: 'tiktok',
  [TrafficSourceCodeEnum.Snapchat]: 'snapchat',
  [TrafficSourceCodeEnum.Other]: 'other',
}

export const usePerformanceFilter = (creatorId?: MaybeRefOrGetter<number | undefined>) => {
  const advertiserStore = useAdvertiserStore()
  const filterStore = useGlobalFilterStore()

  const advertiserId = computed(() => advertiserStore.advertiser?.id)
  const dateFrom = computed(
    () => filterStore.dateRange?.startDate || dayjs().startOf('month').format('YYYY-MM-DD'),
  )
  const dateTo = computed(() => filterStore.dateRange?.endDate || dayjs().format('YYYY-MM-DD'))
  const comparison = computed(() => getComparisonRange(dateFrom.value, dateTo.value))
  const previousDateFrom = computed(() => comparison.value.from)
  const previousDateTo = computed(() => comparison.value.to)
  const trafficSource = computed<PerformanceTrafficSource>(() =>
    filterStore.trafficSource ? TRAFFIC_SOURCE_MAP[filterStore.trafficSource] : 'all',
  )
  const creator = computed(() => {
    const id = toValue(creatorId)
    return id != null ? String(id) : undefined
  })

  const metricsFilter = computed<PerformanceMetricsFilter | undefined>(() => {
    if (advertiserId.value == null) {
      return undefined
    }
    return {
      advertiserId: advertiserId.value,
      dateFrom: dateFrom.value,
      dateTo: dateTo.value,
      previousDateFrom: previousDateFrom.value,
      previousDateTo: previousDateTo.value,
      trafficSource: trafficSource.value,
      ...(creator.value ? { creatorId: creator.value } : {}),
    }
  })

  return {
    advertiserId,
    dateFrom,
    dateTo,
    previousDateFrom,
    previousDateTo,
    trafficSource,
    creatorId: creator,
    metricsFilter,
  }
}
